import { useTranslation } from 'react-i18next'
import { object } from 'yup'
import { useFeatureFlags } from '@node-space/hooks'
import { Option as SelectOption } from '@node-space/storybook-components/dist/Select'
import { CountryCodes } from 'constants/Countries'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useFormSetup } from 'hooks/forms/useFormSetup'
import { useYupFields } from 'hooks/forms/useYupFields'
import { FormFieldsType, FormSchemaType } from 'types/formField'
import { BusinessAddressFormKeys } from './constants'
import { BusinessAddressFormData, BusinessAddressFormKey } from './types'

export type BusinessAddressSchema = FormSchemaType<BusinessAddressFormKey>
export type BusinessAddressFields = FormFieldsType<BusinessAddressFormKey>

export const useBusinessAddressForm = (
  countryOptions: SelectOption[],
  savedData: BusinessAddressFormData
) => {
  const { t } = useTranslation()
  const { enableSignupUk } = useFeatureFlags()
  const { getYupField, getRequiredYupField } = useYupFields()

  const formKeys: BusinessAddressFormKey[] = Object.values(BusinessAddressFormKeys)

  const defaultValues = formKeys?.reduce((defaultValuesObj, key: BusinessAddressFormKey) => {
    const isCountryCodeField = key === BusinessAddressFormKeys.COUNTRY_CODE
    let value = savedData?.[key]
    if (!enableSignupUk && isCountryCodeField && value === CountryCodes?.UK) {
      value = ''
    }
    defaultValuesObj[key] = value ?? ''
    return defaultValuesObj
  }, {} as BusinessAddressFormData)

  const getFieldSchema = (key: BusinessAddressFormKey) => {
    const label = t(`formFields.${key}`)
    switch (key) {
      case BusinessAddressFormKeys.ADDRESS_2:
        return getYupField({ label })
      case BusinessAddressFormKeys.COUNTRY_CODE:
        return getRequiredYupField({
          label,
          requiredErrorMessage: t('formFields.countryIsRequired'),
        })
      default:
        return getRequiredYupField({ label })
    }
  }

  const schemaObj = formKeys?.reduce((schemaObj, key: BusinessAddressFormKey) => {
    schemaObj[key] = getFieldSchema(key)
    return schemaObj
  }, {} as BusinessAddressSchema)

  const formSchema = object<BusinessAddressSchema>(schemaObj)

  const { form, formValues } = useFormSetup<BusinessAddressFormData>(formSchema, defaultValues)
  const formErrors = form?.formState?.errors || {}
  const isFormDirty = form?.formState?.isDirty

  const { setInputField, setSelectField } = useFormFields<BusinessAddressFormKey>(
    formSchema,
    form,
    formValues
  )

  const getFieldProps = (key: BusinessAddressFormKey) => {
    switch (key) {
      case BusinessAddressFormKeys.COUNTRY_CODE:
        return setSelectField({
          key,
          options: countryOptions,
          placeholder: t('formFields.chooseCountry'),
          showLabel: false,
        })
      default:
        return setInputField({ key, useLabelAsPlaceholder: true, showLabel: false })
    }
  }

  const formFields = formKeys?.reduce((fieldsObj, key: BusinessAddressFormKey) => {
    fieldsObj[key] = getFieldProps(key)
    return fieldsObj
  }, {} as BusinessAddressFields)

  const resetForm = () => {
    form?.reset(defaultValues)
  }

  return { form, formValues, formFields, formErrors, isFormDirty, resetForm }
}
